import React from 'react';
import { Box, Paper, Typography, Button } from '@mui/material';
import {
  Vaccines as VaccinesIcon,
  ContentPasteSearch as EmptyIcon,
  ElectricBolt as SeizuresIcon,
  Medication as MedicationIcon,
  Restaurant as NutritionIcon,
  Science as LabIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import AppStyles from '../styles/AppStyles';

const iconMap = {
  vaccination: <VaccinesIcon />,
  seizures: <SeizuresIcon />,
  medication: <MedicationIcon />,
  nutrition: <NutritionIcon />,
  lab: <LabIcon />,
  default: <EmptyIcon />,
};

/**
 * NoEntries - unified empty state for pages that have no data for the current pet.
 * Usage: <NoEntries title="No vaccinations yet" description="Add a vaccination log to see it here." icon="vaccination" />
 */
export default function NoEntries({
  title,
  description,
  icon = 'default',
  actionLabel,
  onAction,
}) {
  const { t } = useTranslation();
  const Icon = iconMap[icon] || iconMap.default;
  return (
    <Paper
      elevation={8}
      sx={{
        p: 4,
        borderRadius: 3,
        background: 'transparent',
        border: `1px solid ${AppStyles.colors.border.light}`,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: `linear-gradient(90deg, ${AppStyles.colors.secondary.main} 0%, ${AppStyles.colors.primary.main} 100%)`,
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 2 }}>
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${AppStyles.colors.primary.main} 0%, ${AppStyles.colors.secondary.main} 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: AppStyles.colors.primary.contrastText,
            boxShadow: AppStyles.shadows.md,
          }}
        >
          {React.cloneElement(Icon, { fontSize: 'medium' })}
        </Box>

        <Typography
          variant="h6"
          sx={{ color: AppStyles.colors.primary.dark, fontWeight: AppStyles.typography.fontWeight.semibold }}
        >
          {title || t('noEntries.defaultTitle')}
        </Typography>
        <Typography variant="body2" sx={{ color: AppStyles.colors.text.secondary, maxWidth: 540 }}>
          {description || t('noEntries.defaultDescription')}
        </Typography>

        {actionLabel && onAction && (
          <Button
            onClick={onAction}
            variant="contained"
            sx={{
              mt: 1,
              background: `linear-gradient(135deg, ${AppStyles.colors.primary.main} 0%, ${AppStyles.colors.secondary.main} 100%)`,
              color: AppStyles.colors.primary.contrastText,
              textTransform: 'none',
              fontWeight: AppStyles.typography.fontWeight.medium,
              borderRadius: 2,
              px: 3,
            }}
          >
            {actionLabel}
          </Button>
        )}
      </Box>
    </Paper>
  );
}


